import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/shared/components/layout/Header';
import { Sidebar, MenuItem } from '@/shared/components/layout/Sidebar';
import { Footer } from '@/shared/components/layout/Footer';
import { ToastProvider } from '@/shared/components/ui';
import { TabContainer } from '@/app/components/tabs';
import { useAuth } from '@/modules/administracion/auth/model/useAuth';
import { useMenu } from '@/modules/menu/model/useMenu';
import { useAuthStore, GestorSesion, PantallaBloqueo, useLock as useLockHook, ModalAdvertenciaInactividad, useRastreadorActividad, useDetectorSesionMultiple, ModalSesionDuplicada, PantallaSesionCerrada } from '@/modules/administracion/auth';
import { useTabsStore } from '@/app/store/useTabsStore';
import { useTheme } from '@/app/hooks/useTheme';
import logoIcon from '@/shared/icons/logo_icon.svg';
import logoTextLight from '@/shared/icons/logo_text_light.svg';
import logoTextDark from '@/shared/icons/logo_text_dark.svg';
import styles from './DashboardLayout.module.css';

/**
 * DashboardLayout - Layout principal del dashboard
 * 
 * Este componente orquesta:
 * - Header: Barra superior con logo, notificaciones y menú de usuario
 * - Sidebar: Menú lateral dinámico con soporte para N niveles
 * - Content: Área de contenido principal (Outlet de React Router)
 * - Footer: Pie de página opcional
 * 
 * El menú del Sidebar se carga dinámicamente desde la API del backend.
 * Para pasar el menú al Sidebar, necesitas:
 * 
 * 1. Obtener los datos del menú desde tu API (usando React Query, Axios, etc.)
 * 2. Transformar los datos a la estructura MenuItem[]
 * 3. Pasar menuData al componente Sidebar
 * 
 * Ejemplo de uso:
 * ```tsx
 * const { data: menuData } = useQuery({
 *   queryKey: ['menu'],
 *   queryFn: fetchMenuFromAPI
 * });
 * 
 * <DashboardLayout menuData={menuData || []} />
 * ```
 */
export interface DashboardLayoutProps {
  /**
   * Datos del menú dinámico desde la API
   * Si no se proporciona, se usa un menú vacío
   */
  menuData?: MenuItem[];

  /**
   * Avatar del usuario para el Header
   */
  userAvatar?: string;

  /**
   * Nombre del usuario para el Header
   */
  userName?: string;

  /**
   * Items del menú dropdown del usuario
   */
  userMenuItems?: Array<{
    label: string;
    onClick: () => void;
    href?: string;
  }>;

  /**
   * Si se muestra el Footer
   */
  showFooter?: boolean;
}

export const DashboardLayout = ({
  menuData: propMenuData,
  userAvatar,
  userName: propUserName,
  userMenuItems,
  showFooter = true,
}: DashboardLayoutProps) => {
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { lock, isLocked } = useLockHook();  // Lock function + estado bloqueo
  const { menuItems, isLoading: menuLoading, error: menuError, refreshMenu } = useMenu();
  const { user } = useAuthStore();
  const { openTab } = useTabsStore();
  const { theme } = useTheme();
  const { showWarning, countdown, onContinueSession } = useRastreadorActividad(); // Inactivity tracking

  // Feature  // Detector de sesión múltiple
  const {
    sesionDuplicadaDetectada,
    tipo: tipoSesionDuplicada,
    dispositivoConflictivo,
    tabCerradaRemotamente,
    cerrarOtrasSesiones,
  } = useDetectorSesionMultiple();

  // Estado local para controlar visibilidad del modal (solo si NO fue cerrada remotamente)
  const [mostrarModalSesionDuplicada, setMostrarModalSesionDuplicada] = useState(false);

  // Escuchar cambios en sesionDuplicadaDetectada para mostrar el modal
  useEffect(() => {
    // Solo mostrar modal si NO fue cerrada remotamente (en ese caso se muestra PantallaSesionCerrada)
    if (!tabCerradaRemotamente) {
      setMostrarModalSesionDuplicada(sesionDuplicadaDetectada);
    }
  }, [sesionDuplicadaDetectada, tabCerradaRemotamente]);

  // Seleccionar logo según el tema
  const logoText = theme === 'light' ? logoTextDark : logoTextLight;

  // Usar el menú del store si no se proporciona uno como prop
  const menuData = propMenuData || menuItems;
  const userName = propUserName || user?.name;

  // Cerrar sidebar móvil cuando cambia el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsSidebarMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarToggle = () => {
    setIsSidebarMobileOpen((prev) => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarMobileOpen(false);
  };

  const handleSidebarCollapseChange = (isCollapsed: boolean) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  /**
   * Handler para clicks en items del menú sidebar
   * Crea un nuevo tab o activa uno existente
   */
  const handleMenuItemClick = (item: MenuItem) => {
    if (!item.url) return;

    const result = openTab({
      title: item.nombre,
      route: item.url,
      icon: item.icono || undefined,
      closeable: true,
    });

    // Solo navegar si se pudo abrir el tab
    if (result.success) {
      navigate(item.url);
    }

    // TabContainer manejará los toasts (está dentro de ToastProvider)
  };

  // Items del menú del usuario
  const defaultUserMenuItems = userMenuItems || [
    {
      label: 'Perfil',
      onClick: () => {
        navigate('/perfil');
      },
    },
    {
      label: 'Bloquear Aplicación',
      onClick: lock,
    },
    {
      label: 'Configuración',
      onClick: () => {
        navigate('/configuracion');
      },
    },
    {
      label: 'Cerrar sesión',
      onClick: handleLogout,
    },
  ];

  // Si esta tab fue cerrada remotamente, renderizar SOLO PantallaSesionCerrada (bypass-proof)
  if (tabCerradaRemotamente) {
    return (
      <div className={styles.dashboardLayout}>
        <PantallaSesionCerrada />
      </div>
    );
  }

  // Si la app está bloqueada, renderizar SOLO LockScreen (bypass-proof)
  if (isLocked) {
    return (
      <div className={styles.dashboardLayout}>
        <PantallaBloqueo />
      </div>
    );
  }

  // Si hay sesión duplicada detectada (y NO está cerrada remotamente), renderizar SOLO modal (bypass-proof)
  if (mostrarModalSesionDuplicada && !tabCerradaRemotamente) {
    return (
      <div className={styles.dashboardLayout}>
        <ModalSesionDuplicada
          open={true}
          tipo={tipoSesionDuplicada}
          dispositivoConflictivo={dispositivoConflictivo}
          onCerrarEstaTab={handleLogout}
          onCerrarOtraSesion={async () => {
            await cerrarOtrasSesiones();
            setMostrarModalSesionDuplicada(false);
          }}
        />
      </div>
    );
  }

  // Dashboard normal (no bloqueado)
  return (
    <div className={styles.dashboardLayout}>
      <Header
        logoIcon={logoIcon}
        logoText={logoText}
        userAvatar={userAvatar}
        userName={userName}
        userMenuItems={defaultUserMenuItems}
        onSidebarToggle={handleSidebarToggle}
      />

      <div className={styles.pageContent}>
        <Sidebar
          menuData={menuData}
          isMobileOpen={isSidebarMobileOpen}
          onMobileClose={handleSidebarClose}
          isLoading={menuLoading}
          error={menuError}
          onRetry={refreshMenu}
          onCollapseChange={handleSidebarCollapseChange}
          onMenuItemClick={handleMenuItemClick}
        />

        <div className={`${styles.contentWrapper} ${isSidebarCollapsed ? styles.contentWrapperCollapsed : ''}`}>
          <div className={styles.contentInner}>
            <div className={styles.content}>
              <ToastProvider>
                <TabContainer isSidebarCollapsed={isSidebarCollapsed} />
                <GestorSesion />
              </ToastProvider>
            </div>
          </div>

          {showFooter && <Footer isCollapsed={isSidebarCollapsed} />}
        </div>
      </div>

      {/* Inactivity Warning Modal - SOLO si la app NO está bloqueada por sesión múltiple */}
      <ModalAdvertenciaInactividad
        abierto={showWarning}
        cuentaRegresiva={countdown}
        alContinuarSesion={onContinueSession}
      />
    </div>
  );
};
